import { keepPreviousData, useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { leadGenApi } from "./leadGenApi";

// Lead Gen (LeadPilot) CRM: shapes from /api/lead-gen and the React Query hooks for leads, prospects and the dashboard.
// Every query key starts with "leadGen", so a write refreshes every Lead Gen view (LeadPilot's bump()).

export type LeadGenRole = "super_admin" | "company_admin" | "lg_admin" | "team_lead" | "qualifier" | "account_executive" | "lg_user" | "collector";
export type LeadStatus = "New" | "On hold" | "Qualified" | "Disqualified" | "Prospect";
export type TaskStatus = "New" | "Reachout" | "In conversation" | "Won" | "Lost" | "Disqualified";

export type LeadGenPerms = Partial<{
  edit: boolean;
  add: boolean;
  delete: boolean;
  qualify: boolean;
  assign: boolean;
  task: boolean;
  inbound: boolean;
  outbound: boolean;
  users: boolean;
  /** Lead Sources and Settings (Super Admin and Collector) */
  settings: boolean;
  /** all = every lead in scope; page = only the rows on screen, without resumes */
  export: "all" | "page" | "none";
  rollback: boolean;
  platform: boolean;
}>;

/** role is null and perms {} for someone without Lead Gen access. businessUnits are business_units ids. */
export interface LeadGenMe {
  user: { id: string; name: string; email: string };
  role: LeadGenRole | null;
  roleLabel: string | null;
  perms: LeadGenPerms;
  businessUnits: string[];
  verticals: string[];
  countries: string[];
  timezone: string;
}

export interface TemplateField {
  key: string;
  label: string;
  type: "text" | "long" | "number" | "url" | "list" | "select";
  group: "profile" | "poc";
  required: boolean;
  options?: string[];
  /** each inner list is one way to prove a line, e.g. [["page"], ["phone", "location"]] */
  evidence?: string[][];
}

export interface LeadGenVertical {
  id: string;
  name: string;
  businessUnitIds: string[];
}
/** Is the vertical one of these business units'? (one id, or any of several) */
export const verticalIn = (v: LeadGenVertical, bus: string | string[] | null | undefined) =>
  Array.isArray(bus) ? bus.some((b) => v.businessUnitIds.includes(b)) : !!bus && v.businessUnitIds.includes(bus);

export interface LeadGenReference {
  businessUnits: { id: string; name: string }[];
  /** business verticals (Super Admin → Business Verticals); businessUnitIds = those of the companies mapped to it */
  verticals: LeadGenVertical[];
  roles: { key: LeadGenRole; label: string }[];
  leadStatuses: LeadStatus[];
  taskStatuses: TaskStatus[];
  verticalSources: Record<string, string[]>;
  inboundSources: string[];
  outboundSources: string[];
  templates: Record<string, TemplateField[]>;
}

/** /users for pickers. businessUnits/verticals come with it when the server sends them; pickers filter on them then. */
export interface LeadGenUser {
  id: string;
  name: string;
  email?: string;
  role: LeadGenRole | null;
  roleLabel?: string | null;
  businessUnits?: string[];
  verticals?: string[];
}

export type Person = { id: string; name: string } | null;
export type Onboarding = { service: string; note: string; at: string; by: string | null; tz: string | null };

export interface LeadRow {
  id: string;
  leadName: string;
  companyName: string;
  personName: string | null;
  title: string | null;
  email: string | null;
  phone: string | null;
  website: string | null;
  city: string | null;
  state: string | null;
  country: string | null;
  location: string;
  businessVertical: string;
  /** a sub business vertical of the vertical (Super Admin → Business Verticals; a collected lead's category, e.g. Roofing) */
  subVertical: string | null;
  /** business_units ids */
  companyAssigned: string[];
  leadSource: "Inbound" | "Outbound";
  sourceDetails: string | null;
  leadStatus: LeadStatus;
  disqualifyReason: string | null;
  qualifier: Person;
  accountManager: Person;
  assignedTo: { id: string; name: string; as: string } | null;
  taskStatus: TaskStatus | null;
  returned: boolean;
  version: number;
  createdAt: string;
  createdBy: string | null;
  updatedAt: string;
  updatedBy: string | null;
  updatedTz: string;
  convertedAt: string | null;
  onboarding: Onboarding | null;
  deal: { id: number; stage: "Open" | "Won" | "Lost"; service: string; companyKey: string } | null;
  /** set once the prospect is marked Won: the CRM deal, company and contact it was handed to */
  crm: { dealId: number; companyId: string | null; contactId: string | null } | null;
  duplicates: number;
  resumeComplete: number;
  /** a collected branch of a chain: the chain's name */
  branchOf: string | null;
  /** further names the business goes by: searched with its name */
  otherNames: string[];
  /** set while the lead is in the Deleted list */
  deleted: { at: string; by: string | null; tz: string | null; reason: string | null } | null;
  /** required resume fields no source found yet (collected leads): waits for manual verification */
  needsVerification: number;
  resume?: Record<string, string | string[]>;
}

export interface LeadNote {
  id: number;
  body: string;
  author: string;
  role: string;
  at: string;
  tz: string;
  edited: { at: string; by: string } | null;
  /** a deleted note can be undone until purgeAt, then it is gone for good */
  deleted: { at: string; by: string; purgeAt: string } | null;
  mine: boolean;
  /** its author, a Team Lead or an Admin */
  canChange: boolean;
}

export interface LeadDetail extends LeadRow {
  resume: Record<string, string | string[]>;
  template: TemplateField[];
  sources: { name: string; direction: string; at: string }[];
  notes: LeadNote[];
  duplicateMatches: { id: string; company: string; country: string; status: string; website: string; key: string }[];
  createdTz: string;
  /** resume keys whose value is an estimate until a person confirms or edits it */
  estimated: string[];
  /** found by the collector: its gaps are logged */
  scanned: boolean;
  gaps: { key: string; label: string; since: string; source: string | null }[];
}

export type LeadCounts = Record<LeadStatus | "returned" | "verify", number> & { deleted?: number };

export type LeadFacetValue = { key: string; n: number };
/** The filter bar's choices, each counted against the other filters (and the open tab), never its own. */
export interface LeadFacets {
  countries: LeadFacetValue[];
  states: LeadFacetValue[];
  subVerticals: LeadFacetValue[];
}

export interface LeadList {
  items: LeadRow[];
  total: number;
  page: number;
  per: number;
  counts: LeadCounts;
  facets?: LeadFacets;
}

export interface LeadRevision {
  id: number;
  at: string;
  tz: string;
  actor: string;
  role: string;
  action: string;
  changes: { field: string; key?: string; from: unknown; to: unknown; source?: string }[];
  /** the lead right after this event (absent on very old entries) */
  snapshot?: (Record<string, unknown> & { resume?: Record<string, unknown> }) | null;
  restorable: boolean;
}

export interface LeadGenDashboard {
  total: number;
  newThisWeek: number;
  byStatus: Record<string, number>;
  byTask: Record<string, number>;
  returned: number;
  unassignedProspects: number;
  /** won prospects handed to the CRM module */
  handedToCrm: number;
  byBu: { bu: string; n: number }[];
  byVertical: { vertical: string; n: number }[];
  bySource: { type: string; n: number }[];
  bySourceDetail: { name: string | null; n: number }[];
  byAe: { name: string; s: string | null; n: number }[];
  recent: { leadId: string; company: string; action: string; actor: string; at: string; tz: string }[];
}

/** GET /leads filters. Unset values are left out of the query. */
export interface LeadListParams {
  bu?: string | null;
  status?: string;
  returned?: "1";
  deleted?: "1";
  verify?: "1";
  unassigned?: "1";
  /** several values joined with | (a name may hold a comma), like bu, vertical, country, state and subVertical */
  vertical?: string;
  country?: string;
  state?: string;
  subVertical?: string;
  hasWebsite?: "yes" | "no";
  hasPhone?: "yes" | "no";
  hasEmail?: "yes" | "no";
  /** created on or after / on or before, ISO */
  from?: string;
  to?: string;
  source?: string;
  task?: string;
  q?: string;
  page?: number;
  per?: number;
  sort?: string;
  dir?: "asc" | "desc";
}

export type LeadEdit = {
  fields?: Record<string, string | null>;
  resume?: Record<string, unknown>;
  bus?: string[];
  otherNames?: string[];
};

const KEY = "leadGen";
export const invalidateLeadGen = (qc: QueryClient) => qc.invalidateQueries({ queryKey: [KEY] });

/** A mutation that refreshes every Lead Gen view when it succeeds (LeadPilot's bump()). */
export const useLeadGenMutation = <V, R>(fn: (v: V) => Promise<R>) => {
  const qc = useQueryClient();
  return useMutation({ mutationFn: fn, onSuccess: () => invalidateLeadGen(qc) });
};

// ─── reads ───────────────────────────────────────────────────────────────────────

export const useLeadGenMe = (enabled = true) =>
  useQuery({ queryKey: [KEY, "me"], queryFn: () => leadGenApi.get<LeadGenMe>("/me"), staleTime: 5 * 60_000, enabled });

export const useLeadGenReference = () =>
  useQuery({ queryKey: [KEY, "reference"], queryFn: () => leadGenApi.get<LeadGenReference>("/reference"), staleTime: 10 * 60_000 });

/** A business vertical mapped to the viewer's companies (Super Admin → Business Verticals). */
export interface LeadGenCompanyVertical {
  id: string;
  name: string;
  /** the business units its leads go to (of every company mapped to it) */
  businessUnitIds: string[];
  /** the business units of the viewer's companies mapped to it */
  companyBusinessUnitIds: string[];
}
/** The business verticals and business units of the viewer's own companies, whatever their role. */
export const useLeadGenCompanyVerticals = (enabled = true) =>
  useQuery({
    queryKey: [KEY, "companyVerticals"],
    queryFn: () => leadGenApi.get<{ items: LeadGenCompanyVertical[]; businessUnits: { id: string; name: string }[] }>("/company-verticals"),
    staleTime: 10 * 60_000,
    enabled,
  });

export interface LeadGenSubVertical {
  id: string;
  name: string;
  vertical: string;
}
/** The states of the given countries (names), from NetSuite; nothing until a country is picked. */
export const useLeadGenNetsuiteStates = (countries: string[]) =>
  useQuery({
    queryKey: [KEY, "netsuiteStates", countries],
    queryFn: () =>
      leadGenApi.get<{ items: { name: string; country: string }[]; unavailable?: boolean }>("/netsuite-states", { country: countries.join("|") }),
    staleTime: 30 * 60_000,
    enabled: countries.length > 0,
  });

/** The sub business verticals of some of the viewer's companies' verticals; of all of them when `verticals` is empty. */
export const useLeadGenSubVerticals = (verticals: string[], enabled = true) =>
  useQuery({
    queryKey: [KEY, "subVerticals", verticals],
    queryFn: () => leadGenApi.get<{ items: LeadGenSubVertical[] }>("/sub-verticals", { vertical: verticals.join("|") }).then((r) => r.items),
    staleTime: 10 * 60_000,
    // a sub vertical goes with its vertical: nothing to fetch until one is picked
    enabled: enabled && verticals.length > 0,
  });

export const useLeadGenUsers = (enabled = true) =>
  useQuery({
    queryKey: [KEY, "users"],
    // LeadPilot answered { items }; accept a bare array too
    queryFn: async () => {
      const r = await leadGenApi.get<LeadGenUser[] | { items: LeadGenUser[] }>("/users");
      return Array.isArray(r) ? r : r.items;
    },
    enabled,
    staleTime: 5 * 60_000,
  });

export const useLeadGenLeads = (params: LeadListParams, enabled = true) =>
  useQuery({
    queryKey: [KEY, "leads", params],
    queryFn: () => leadGenApi.get<LeadList>("/leads", { ...params, bu: params.bu ?? undefined }),
    placeholderData: keepPreviousData,
    enabled,
  });

export const useLeadGenLead = (id: string | null | undefined) =>
  useQuery({ queryKey: [KEY, "lead", id], queryFn: () => leadGenApi.get<LeadDetail>(`/leads/${id}`), enabled: !!id });

export const useLeadHistory = (id: string, enabled = true) =>
  useQuery({
    queryKey: [KEY, "history", id],
    queryFn: () => leadGenApi.get<{ items: LeadRevision[] }>(`/leads/${id}/history`).then((r) => r.items),
    enabled,
  });

export const useLeadGenDashboard = (bu: string | null) =>
  useQuery({
    queryKey: [KEY, "dashboard", bu],
    queryFn: () => leadGenApi.get<LeadGenDashboard>("/dashboard", { bu: bu ?? undefined }),
    placeholderData: keepPreviousData,
  });

// ─── writes ──────────────────────────────────────────────────────────────────────

type Ok = { ok: boolean; unchanged?: boolean };

/** A write that refreshes every Lead Gen view when it succeeds. */
const useWrite = <V, R = Ok>(fn: (v: V) => Promise<R>) => {
  const qc = useQueryClient();
  return useMutation({ mutationFn: fn, onSuccess: () => invalidateLeadGen(qc) });
};

/**
 * Edit a lead's details, companies, other names or resume. `version` is the one the editor saw: the server answers
 * with a conflict message when someone else saved in between. The saved row is merged into the cached lead and lists
 * right away, so a second edit straight after sends the new version.
 */
export const useSaveLead = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: LeadEdit & { id: string; version: number }) =>
      leadGenApi.patch<{ changed: number; lead: LeadRow }>(`/leads/${id}`, body),
    onSuccess: (r, { id }) => {
      qc.setQueryData<LeadDetail>([KEY, "lead", id], (d) => (d ? { ...d, ...r.lead } : d));
      qc.setQueriesData<LeadList>({ queryKey: [KEY, "leads"] }, (d) =>
        d ? { ...d, items: d.items.map((x) => (x.id === id ? { ...x, ...r.lead } : x)) } : d,
      );
      void invalidateLeadGen(qc);
    },
    // a conflict or a rejected value: show what the server has now
    onError: () => void invalidateLeadGen(qc),
  });
};

export type NewLead = {
  companyName: string;
  personName?: string;
  title?: string;
  email?: string;
  phone?: string;
  website?: string;
  city?: string;
  country?: string;
  leadSource: "Inbound" | "Outbound";
  sourceDetails?: string;
  businessVertical: string;
  companyAssigned: string[];
  /** Prospects page only: the new lead is qualified and goes straight to this Account Manager */
  accountManagerId?: string;
};

export const useCreateLead = () =>
  useWrite((b: NewLead) =>
    leadGenApi.post<{ id: string; duplicateOf?: { id: string; key: string } }>(b.accountManagerId ? "/prospects" : "/leads", b),
  );

export const useDeleteLead = () => useWrite(({ id, reason }: { id: string; reason?: string }) => leadGenApi.delete<Ok>(`/leads/${id}`, { reason }));
export const useUndeleteLead = () => useWrite((id: string) => leadGenApi.post<Ok>(`/leads/${id}/undelete`));
export const useSendBackLead = () =>
  useWrite(({ id, ...b }: { id: string; to: "Qualified" | "On hold" | "New"; reason: string }) => leadGenApi.post<Ok>(`/leads/${id}/send-back`, b));

export type Decision = "Qualified" | "Disqualified" | "On hold" | "New";
export const useQualifyLead = () =>
  useWrite(({ id, ...b }: { id: string; decision: Decision; reason?: string }) => leadGenApi.post<Ok>(`/leads/${id}/qualify`, b));

/** convert: a qualified lead becomes a prospect; assign: a prospect gets another Account Manager. */
export const useAssignAccountManager = () =>
  useWrite(({ id, mode, accountManagerId }: { id: string; mode: "convert" | "assign"; accountManagerId: string }) =>
    leadGenApi.post<Ok>(`/leads/${id}/${mode}`, { accountManagerId }),
  );
export const useAssignQualifier = () =>
  useWrite(({ id, qualifierId }: { id: string; qualifierId: string | null }) => leadGenApi.post<Ok>(`/leads/${id}/qualifier`, { qualifierId }));

/** What Won needs to hand a prospect to the CRM module: the CRM company's tenant, its contact and the first deal. */
export interface CrmHandoffInput {
  companyId: string;
  contact: { firstName: string; lastName: string; email: string; phone: string; designation: string };
  deal: { dealName: string; industry: string; categoryId: string; expectedCloseDate: string; dealAmount: number; dealCurrency: string };
}
export interface CrmHandoffOptions {
  /** One Vertoz companies of the lead's business units with CRM on */
  companies: { id: string; name: string }[];
  contact: CrmHandoffInput["contact"];
  deal: { dealName: string };
}
export const useCrmHandoffOptions = (id: string | null) =>
  useQuery({ queryKey: [KEY, "crmHandoff", id], queryFn: () => leadGenApi.get<CrmHandoffOptions>(`/leads/${id}/crm-handoff`), enabled: !!id });

/** Task status (Account Managers). Won carries the CRM handoff (company, contact, deal). */
export const useLeadTask = () =>
  useWrite(({ id, ...b }: { id: string; status: string; note?: string; crm?: CrmHandoffInput }) => leadGenApi.post<Ok>(`/leads/${id}/task`, b));

export const useRestoreRevision = () =>
  useWrite(({ id, revisionId }: { id: string; revisionId: number }) => leadGenApi.post<{ changed: number }>(`/leads/${id}/restore`, { revisionId }));

export const useAddNote = () => useWrite(({ id, body }: { id: string; body: string }) => leadGenApi.post<Ok>(`/leads/${id}/notes`, { body }));
export const useEditNote = () =>
  useWrite(({ id, noteId, body }: { id: string; noteId: number; body: string }) => leadGenApi.patch<Ok>(`/leads/${id}/notes/${noteId}`, { body }));
export const useDeleteNote = () =>
  useWrite(({ id, noteId }: { id: string; noteId: number }) => leadGenApi.delete<Ok & { purgeAt: string }>(`/leads/${id}/notes/${noteId}`));
export const useRestoreNote = () =>
  useWrite(({ id, noteId }: { id: string; noteId: number }) => leadGenApi.post<Ok>(`/leads/${id}/notes/${noteId}/restore`));
